import { Check, ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

export interface AbaDaLista<T extends string> {
  value: T;
  label: string;
  /** Quantos a aba mostra — aparece só na aba ativa (docs/PADROES_UI_WEB.md, Tabs). */
  contagem?: number;
  /**
   * Número que pede ação ("Não lidas", "Falhas", "Aguardando você"): fica à vista mesmo
   * com a aba inativa. `sempre` mantém o selo também na ativa.
   */
  alerta?: { n: number; variante?: 'default' | 'destructive'; sempre?: boolean };
}

export interface AbasDaListaProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  abas: ReadonlyArray<AbaDaLista<T>>;
  /** Nome acessível do seletor do celular ("Situação", "Fila"). */
  nome?: string;
}

function Selo({ alerta }: { alerta: NonNullable<AbaDaLista<string>['alerta']> }) {
  return (
    <Badge
      variant={alerta.variante ?? 'default'}
      className="h-[1.125rem] min-w-[1.125rem] justify-center px-1 text-[11px] leading-none"
    >
      {alerta.n}
    </Badge>
  );
}

const mostraAlerta = (a: AbaDaLista<string>, ativa: boolean) => !!a.alerta?.n && (a.alerta.sempre || !ativa);

/**
 * As abas de filtro das telas de lista (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.3). No
 * computador, abas sublinhadas; no celular, um seletor ("Todos · 14 ▾"), porque a fileira
 * de abas não cabe em 360px e rolar de lado esconde as últimas sem nenhuma pista.
 */
export function AbasDaLista<T extends string>({ value, onValueChange, abas, nome = 'Mostrar' }: AbasDaListaProps<T>) {
  const largo = useMediaQuery('(min-width: 640px)');
  const atual = abas.find((a) => a.value === value) ?? abas[0];

  if (!largo) {
    // Um alerta de OUTRA aba também aparece no gatilho: fechado, o seletor não pode esconder
    // as falhas ou as intimações que esperam o advogado.
    const alertaFora = abas.find((a) => a.value !== atual.value && mostraAlerta(a, false));
    // O nome falado diz o que o gatilho mostra: a aba, a contagem e o que pede ação.
    const falado = [
      `${nome}: ${atual.label}`,
      atual.contagem !== undefined ? String(atual.contagem) : null,
      mostraAlerta(atual, true) ? `${atual.alerta!.n} ${atual.label.toLowerCase()}` : null,
      alertaFora ? `${alertaFora.alerta!.n} em ${alertaFora.label}` : null,
    ]
      .filter(Boolean)
      .join(', ');
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={falado}
            className="h-11 min-w-0 shrink justify-between gap-2 px-3 font-normal"
          >
            <span className="truncate">{atual.label}</span>
            {atual.contagem !== undefined && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                {atual.contagem}
              </span>
            )}
            {mostraAlerta(atual, true) && <Selo alerta={atual.alerta!} />}
            {alertaFora && <Selo alerta={alertaFora.alerta!} />}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[12rem]">
          {abas.map((a) => (
            <DropdownMenuItem
              key={a.value}
              role="menuitemradio"
              aria-checked={a.value === atual.value}
              onSelect={() => onValueChange(a.value)}
              className="min-h-11 gap-2"
            >
              <Check className={cn('h-4 w-4', a.value === atual.value ? 'opacity-100' : 'opacity-0')} aria-hidden />
              <span className="flex-1">{a.label}</span>
              {mostraAlerta(a, a.value === atual.value) && <Selo alerta={a.alerta!} />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Tabs value={value} onValueChange={(v) => onValueChange(v as T)}>
      <TabsList className="h-11">
        {abas.map((a) => {
          const ativa = a.value === value;
          return (
            <TabsTrigger key={a.value} value={a.value} className="gap-1.5">
              {a.label}
              {ativa && a.contagem !== undefined && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                  {a.contagem}
                </span>
              )}
              {mostraAlerta(a, ativa) && <Selo alerta={a.alerta!} />}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
